import { useEffect } from "react";
import { specialMachines } from "@/data/specialMachines";
import { useLanguage } from "@/contexts/LanguageContext";

const SITE_ORIGIN = "https://kocevar.eu";
const JSONLD_ID = "jsonld-featured-projects-itemlist";

const isValidHref = (href: unknown): href is string =>
  typeof href === "string" &&
  href.trim().length > 0 &&
  (href.startsWith("/") || /^https?:\/\//.test(href));

const isValidImage = (image: unknown): image is string =>
  typeof image === "string" &&
  image.trim().length > 0 &&
  (image.startsWith("/") || /^https?:\/\//.test(image));

const toAbsolute = (path: string) =>
  /^https?:\/\//.test(path) ? path : `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Injects a JSON-LD ItemList describing the Featured Projects carousel
 * items with their canonical URLs, localized titles and thumbnail images.
 *
 * Mirrors the same data source and validation as <FeaturedProjects />.
 * Mount once on the homepage.
 */
const FeaturedProjectsJsonLd = () => {
  const { lang } = useLanguage();

  useEffect(() => {
    const rawEntries = [
      ...specialMachines,
      {
        en: "Spot welding machine for welding sinks into counters",
        sl: "Stroj za točkovno varjenje pomivalnih korit v pulte",
        image: "/solutions/sink-welding-kocevar.png",
        href: "/solutions/spot-welding-sinks",
      },
    ];

    const entries = rawEntries.filter(
      (e) =>
        isValidHref(e.href) &&
        typeof e.en === "string" && e.en.trim().length > 0 &&
        typeof e.sl === "string" && e.sl.trim().length > 0,
    );

    if (entries.length === 0) {
      document.getElementById(JSONLD_ID)?.remove();
      return;
    }

    const data = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: lang === "sl" ? "Izbrani projekti" : "Featured Projects",
      inLanguage: lang === "sl" ? "sl-SI" : "en-US",
      numberOfItems: entries.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: entries.map((entry, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: toAbsolute(entry.href),
        item: {
          "@type": "Product",
          name: lang === "sl" ? entry.sl : entry.en,
          url: toAbsolute(entry.href),
          ...(isValidImage(entry.image) ? { image: toAbsolute(entry.image) } : {}),
          brand: { "@type": "Brand", name: "Kočevar" },
          manufacturer: {
            "@type": "Organization",
            name: "Kočevar d.o.o.",
            url: SITE_ORIGIN,
          },
        },
      })),
    };

    let el = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = JSONLD_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    return () => {
      document.getElementById(JSONLD_ID)?.remove();
    };
  }, [lang]);

  return null;
};

export default FeaturedProjectsJsonLd;
